import React, { useState, useEffect } from 'react';
import { getBackendUrl } from '../utils/api';
import storage from '../utils/storage';
import { 
  Download, 
  Upload, 
  RefreshCw, 
  Clock, 
  Database,
  CheckCircle,
  AlertCircle,
  Loader2,
  Calendar,
  HardDrive,
  Settings
} from 'lucide-react';

const BACKEND_URL = getBackendUrl() || '';

const BackupManager = () => {
  const [tools, setTools] = useState([]);
  const [backups, setBackups] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [selectedTools, setSelectedTools] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [expandedBackup, setExpandedBackup] = useState(null);

  const getAuthHeaders = () => {
    const token = storage.getItem('token');
    return {
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch tools, backups, and status in parallel
      const [toolsRes, backupsRes, statusRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/backup/tools`, getAuthHeaders()),
        fetch(`${BACKEND_URL}/api/backup/list?limit=60`, getAuthHeaders()),
        fetch(`${BACKEND_URL}/api/backup/status`, getAuthHeaders())
      ]);

      if (toolsRes.ok) {
        const toolsData = await toolsRes.json();
        setTools(toolsData.tools || []);
      }

      if (backupsRes.ok) {
        const backupsData = await backupsRes.json();
        setBackups(backupsData.backups || []);
      }

      if (statusRes.ok) {
        const statusData = await statusRes.json();
        setStatus(statusData);
      }
    } catch (error) {
      console.error('Error fetching backup data:', error);
      setMessage({ type: 'error', text: 'Σφάλμα φόρτωσης δεδομένων' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async (toolIds = null) => {
    setActionLoading(true);
    setMessage({ type: '', text: '' });
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/backup/create`, {
        method: 'POST',
        ...getAuthHeaders(),
        body: JSON.stringify({ tools: toolIds })
      });

      const data = await response.json();
      
      if (data.success) {
        setMessage({ 
          type: 'success', 
          text: `✅ Backup δημιουργήθηκε: ${data.total_records.toLocaleString()} εγγραφές` 
        });
        fetchData();
      } else {
        setMessage({ type: 'error', text: data.error || 'Σφάλμα δημιουργίας backup' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Σφάλμα δημιουργίας backup' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestore = async (backupId, toolIds = null, dryRun = false) => {
    if (!dryRun && !window.confirm('Είστε σίγουροι ότι θέλετε να επαναφέρετε; Τα τρέχοντα δεδομένα θα αντικατασταθούν.')) {
      return;
    }

    setActionLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${BACKEND_URL}/api/backup/restore`, {
        method: 'POST',
        ...getAuthHeaders(),
        body: JSON.stringify({
          backup_id: backupId,
          tools: toolIds,
          dry_run: dryRun
        })
      });

      const data = await response.json();

      if (data.success) {
        if (dryRun) {
          setMessage({ 
            type: 'info', 
            text: `📋 Θα επαναφερθούν ${data.total_restored.toLocaleString()} εγγραφές` 
          });
        } else {
          setMessage({ 
            type: 'success', 
            text: `✅ Επαναφορά ολοκληρώθηκε: ${data.total_restored.toLocaleString()} εγγραφές` 
          });
          fetchData();
        }
      } else {
        setMessage({ type: 'error', text: data.error || 'Σφάλμα επαναφοράς' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Σφάλμα επαναφοράς' });
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('el-GR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const toggleToolSelection = (toolId) => {
    setSelectedTools(prev => 
      prev.includes(toolId) 
        ? prev.filter(id => id !== toolId)
        : [...prev, toolId]
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Φόρτωση...</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <HardDrive className="w-8 h-8" />
              Σύστημα Backup & Επαναφοράς
            </h1>
            <p className="text-blue-100 mt-1">
              Αυτόματο backup κάθε 12 ώρες • Διατήρηση 30 ημερών • Επιλεκτική επαναφορά
            </p>
          </div>
          <button
            onClick={() => handleCreateBackup()}
            disabled={actionLoading}
            className="px-6 py-3 bg-white text-blue-700 rounded-lg font-semibold hover:bg-blue-50 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {actionLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Download className="w-5 h-5" />
            )}
            Δημιουργία Backup Τώρα
          </button>
        </div>
      </div>

      {/* Status */}
      {status && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 shadow border-l-4 border-green-500">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Κατάσταση</span>
            </div>
            <p className="text-2xl font-bold text-gray-800 mt-1">Ενεργό</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow border-l-4 border-blue-500">
            <div className="flex items-center gap-2 text-blue-700">
              <Database className="w-5 h-5" />
              <span className="font-medium">Συνολικά Backups</span>
            </div>
            <p className="text-2xl font-bold text-gray-800 mt-1">{status.total_backups}</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow border-l-4 border-purple-500">
            <div className="flex items-center gap-2 text-purple-700">
              <Clock className="w-5 h-5" />
              <span className="font-medium">Διάστημα</span>
            </div>
            <p className="text-2xl font-bold text-gray-800 mt-1">12 ώρες</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow border-l-4 border-orange-500">
            <div className="flex items-center gap-2 text-orange-700">
              <Calendar className="w-5 h-5" />
              <span className="font-medium">Διατήρηση</span>
            </div>
            <p className="text-2xl font-bold text-gray-800 mt-1">30 ημέρες</p>
          </div>
        </div>
      )}

      {/* Message */}
      {message.text && (
        <div className={`p-4 rounded-lg flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-100 text-green-800' :
          message.type === 'error' ? 'bg-red-100 text-red-800' :
          'bg-blue-100 text-blue-800'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> :
           message.type === 'error' ? <AlertCircle className="w-5 h-5" /> :
           <RefreshCw className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      {/* Tools Grid */}
      <div className="bg-white rounded-xl shadow p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Διαθέσιμα Tools για Backup
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {tools.map((tool) => (
            <div
              key={tool.id}
              onClick={() => toggleToolSelection(tool.id)}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                selectedTools.includes(tool.id)
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-2xl">{tool.icon}</span>
                <div>
                  <p className="font-medium text-gray-800 text-sm">{tool.name}</p>
                  <p className="text-xs text-gray-500">{tool.collections?.length || 0} collections</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        {selectedTools.length > 0 && (
          <div className="mt-4 flex gap-2">
            <button
              onClick={() => handleCreateBackup(selectedTools)}
              disabled={actionLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Backup Επιλεγμένων ({selectedTools.length})
            </button>
            <button
              onClick={() => setSelectedTools([])}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Καθαρισμός
            </button>
          </div>
        )}
      </div>

      {/* Backups List */}
      <div className="bg-white rounded-xl shadow">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Database className="w-5 h-5" />
              Διαθέσιμα Backups ({backups.length})
            </h2>
            <button
              onClick={fetchData}
              disabled={loading}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
        
        <div className="divide-y max-h-[600px] overflow-y-auto">
          {backups.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              Δεν υπάρχουν διαθέσιμα backups
            </div>
          ) : (
            backups.map((backup) => (
              <div key={backup.backup_id} className="p-4 hover:bg-gray-50">
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedBackup(expandedBackup === backup.backup_id ? null : backup.backup_id)}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${
                      backup.backup_type === 'auto' ? 'bg-blue-100' : 'bg-green-100'
                    }`}>
                      {backup.backup_type === 'auto' ? (
                        <Clock className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Download className="w-5 h-5 text-green-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">
                        {formatDate(backup.created_at)}
                      </p>
                      <p className="text-sm text-gray-500">
                        {backup.backup_type === 'auto' ? 'Αυτόματο' : 'Χειροκίνητο'} • 
                        {backup.total_records?.toLocaleString()} εγγραφές • 
                        {backup.tools?.length} tools
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRestore(backup.backup_id, null, true);
                      }}
                      disabled={actionLoading}
                      className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
                    >
                      Προεπισκόπηση
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRestore(backup.backup_id);
                      }}
                      disabled={actionLoading}
                      className="px-3 py-1 text-sm bg-orange-100 text-orange-700 hover:bg-orange-200 rounded font-medium"
                    >
                      <Upload className="w-4 h-4 inline mr-1" />
                      Επαναφορά Όλων
                    </button>
                  </div>
                </div>
                
                {/* Expanded view with tools */}
                {expandedBackup === backup.backup_id && (
                  <div className="mt-4 pl-14 grid grid-cols-2 md:grid-cols-3 gap-2">
                    {backup.tools?.map((tool) => (
                      <div 
                        key={tool.id}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          <span>{tool.icon}</span>
                          <div>
                            <p className="text-sm font-medium">{tool.name}</p>
                            <p className="text-xs text-gray-500">{tool.records?.toLocaleString()} εγγραφές</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRestore(backup.backup_id, [tool.id])}
                          disabled={actionLoading}
                          className="p-1 text-orange-600 hover:bg-orange-100 rounded"
                          title={`Επαναφορά ${tool.name}`}
                        >
                          <Upload className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default BackupManager;
